import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Trash2, Save, Loader2, AlertCircle, Check,
  ChevronRight, Lock, X as XIcon, Sparkles, Eye, ArrowLeft,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { EntityShell } from "../components/shell";

/**
 * /settings/roles — owner/admin permission grid editor (CONTRACTS §18).
 *
 * Reads / writes via the §17 tool engine, so every change is audited
 * + undoable for 24h. We deliberately bind to /api/hidro/tools/call
 * rather than direct mongo access — this is the same path Hidro itself
 * uses, which means the UI can never escalate beyond what the asker is
 * permitted to do.
 */
const COMMON_RESOURCES = [
  "customers",
  "customers.financials",
  "leads",
  "jobcards",
  "tickets",
  "meetings",
  "trips",
  "users",
  "roles",
  "billing",
  "ai_tools",
];
const LEVEL_OPTIONS = ["read", "write", "admin"];

export default function RolesEditor() {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ id: "", name: "", inherits_from: [] });
  const [recentActions, setRecentActions] = useState([]);

  const isOwner = user?.role === "owner";
  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/hidro/tools/call", {
        name: "list_roles",
        inputs: {},
      });
      setRoles(data?.result?.roles || []);
      try {
        const audit = await api.get("/hidro/tools/audit?limit=8");
        setRecentActions(
          (audit.data?.items || []).filter((a) => ["assign_role_to_user", "create_role", "update_role_permissions", "delete_role"].includes(a.tool)),
        );
      } catch (_e) { /* noop */ }
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load roles. Owner/admin access required.");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startEdit = (role) => {
    setEditingId(role.id);
    setForm({
      id: role.id,
      name: role.name,
      system: role.system,
      inherits_from: role.inherits_from || [],
      permissions: JSON.parse(JSON.stringify(role.permissions || {})),
    });
    setErr("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(null);
  };

  const togglePermLevel = (resource, level) => {
    setForm((f) => {
      const cur = new Set(f.permissions[resource] || []);
      if (cur.has(level)) cur.delete(level);
      else cur.add(level);
      const next = { ...f.permissions };
      if (cur.size === 0) delete next[resource];
      else next[resource] = Array.from(cur);
      return { ...f, permissions: next };
    });
  };

  const addResource = (resource) => {
    if (!resource) return;
    setForm((f) => ({
      ...f,
      permissions: { ...f.permissions, [resource]: f.permissions[resource] || ["read"] },
    }));
  };

  const removeResource = (resource) => {
    setForm((f) => {
      const next = { ...f.permissions };
      delete next[resource];
      return { ...f, permissions: next };
    });
  };

  const toggleParent = (parentId) => {
    setForm((f) => {
      const cur = new Set(f.inherits_from || []);
      if (cur.has(parentId)) cur.delete(parentId);
      else if (cur.size < 3) cur.add(parentId);
      return { ...f, inherits_from: Array.from(cur) };
    });
  };

  const save = async () => {
    if (!form) return;
    if (form.id === "owner" && !isOwner) {
      setErr("Only owner can edit the owner role");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await api.post("/hidro/tools/call", {
        name: "update_role_permissions",
        inputs: {
          role_id: form.id,
          permissions: form.permissions,
          inherits_from: form.inherits_from || [],
        },
        reason: "owner edit via /settings/roles",
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      cancelEdit();
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const createRole = async () => {
    if (!createForm.id || !createForm.name) {
      setErr("Role id + name required");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await api.post("/hidro/tools/call", {
        name: "create_role",
        inputs: {
          id: createForm.id.toLowerCase().trim(),
          name: createForm.name.trim(),
          inherits_from: createForm.inherits_from || [],
          permissions: {},
          description: createForm.description || "",
        },
        reason: "owner create via /settings/roles",
      });
      setCreateOpen(false);
      setCreateForm({ id: "", name: "", inherits_from: [] });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Create failed");
    }
    setSaving(false);
  };

  const [pendingDelete, setPendingDelete] = useState(null);
  const deleteRole = (rid) => setPendingDelete(rid);
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.post("/hidro/tools/call", {
        name: "delete_role",
        inputs: { role_id: pendingDelete },
        reason: "owner delete via /settings/roles",
      });
      setPendingDelete(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed");
    }
  };

  const undoAction = async (actionId) => {
    try {
      await api.post(`/hidro/tools/${actionId}/undo`, {});
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Undo failed");
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-xs text-[#647087]">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading roles…
      </div>
    );
  }

  if (err && !roles.length) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Link to=".." className="hv-btn hv-btn-ghost text-xs"><ArrowLeft className="w-3 h-3" /> Back</Link>
        <div className="mt-4 text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-3 text-sm" data-testid="roles-error">
          <AlertCircle className="w-4 h-4 inline mr-2" /> {err}
        </div>
      </div>
    );
  }

  return (
    <EntityShell
      kindLabel="Account · Roles"
      title="Roles & permissions"
      backTo=".."
      actions={
        <>
          {savedFlash && <span className="text-[10px] text-emerald-300 inline-flex items-center gap-1" data-testid="roles-saved-pill"><Check className="w-3 h-3" /> Saved</span>}
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            disabled={!isOwner}
            className="hv-btn hv-btn-primary text-xs"
            title={isOwner ? "Create a custom role" : "Owner only"}
            data-testid="roles-create"
          >
            <Plus className="w-3 h-3" /> New role
          </button>
        </>
      }
    >
    <div className="space-y-5 max-w-6xl" data-testid="roles-editor-page">
      <p className="text-sm text-[#a0aabf]">
        Every role-grid edit is audited + reversible for 24h. Hidro uses
        this exact data when deciding what tools each user can call —
        there's no second source of truth.
      </p>

      {err && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2" data-testid="roles-err-banner">
          {err}
        </div>
      )}

      {/* Inline 2-step delete confirmation (Trust Contract Rule 2) */}
      {pendingDelete && (
        <div className="hv-card border-rose-500/40 bg-rose-500/5 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="roles-delete-confirm">
          <span className="text-xs text-rose-200">
            Delete custom role <strong>"{pendingDelete}"</strong>?  Cannot delete if any user is assigned.
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPendingDelete(null)} className="hv-btn hv-btn-ghost text-xs" data-testid="roles-delete-cancel">Cancel</button>
            <button onClick={confirmDelete} className="hv-btn hv-btn-primary text-xs bg-rose-500 hover:bg-rose-400" data-testid="roles-delete-confirm-btn">Confirm delete</button>
          </div>
        </div>
      )}

      {/* Create form */}
      {createOpen && (
        <div className="hv-card p-4 border-[#A78BFA]/30 space-y-3" data-testid="roles-create-form">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#C4B5FD] font-bold">New custom role</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text" placeholder="id (kebab-case, e.g. tech-lead)"
              value={createForm.id}
              onChange={(e) => setCreateForm({ ...createForm, id: e.target.value })}
              className="hv-input text-sm"
              data-testid="roles-create-id"
            />
            <input
              type="text" placeholder="Display name (e.g. Tech Lead)"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              className="hv-input text-sm"
              data-testid="roles-create-name"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Inherits from (max 3)</div>
            <div className="flex flex-wrap gap-1.5">
              {roles.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setCreateForm({
                    ...createForm,
                    inherits_from: createForm.inherits_from.includes(r.id)
                      ? createForm.inherits_from.filter((x) => x !== r.id)
                      : (createForm.inherits_from.length < 3 ? [...createForm.inherits_from, r.id] : createForm.inherits_from),
                  })}
                  className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                    createForm.inherits_from.includes(r.id)
                      ? "bg-[#A78BFA]/20 text-[#C4B5FD] border-[#A78BFA]/50"
                      : "bg-white/5 text-[#a0aabf] border-white/10 hover:border-[#A78BFA]/30"
                  }`}
                >{r.name}</button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setCreateOpen(false)} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
            <button onClick={createRole} disabled={saving} className="hv-btn hv-btn-primary text-xs" data-testid="roles-create-submit">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Create
            </button>
          </div>
        </div>
      )}

      {/* Role list */}
      <div className="space-y-3">
        {roles.map((role) => {
          const isEditingThis = editingId === role.id;
          const canEdit = isOwner || (isAdmin && role.id !== "owner");
          return (
            <div key={role.id} className="hv-card p-4" data-testid={`role-${role.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-bold flex items-center gap-2">
                    <span>{role.name}</span>
                    {role.system && <span className="text-[10px] uppercase font-mono px-1.5 py-0 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300 inline-flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> system</span>}
                    <span className="text-[10px] font-mono text-[#647087]">{role.id}</span>
                  </div>
                  {role.description && <div className="text-xs text-[#a0aabf] mt-0.5">{role.description}</div>}
                  {(role.inherits_from || []).length > 0 && (
                    <div className="text-[10px] text-[#647087] mt-1">
                      Inherits: {role.inherits_from.join(", ")}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {!isEditingThis && canEdit && (
                    <button
                      onClick={() => startEdit(role)}
                      className="hv-btn hv-btn-ghost text-xs"
                      data-testid={`role-edit-${role.id}`}
                    >Edit</button>
                  )}
                  {!role.system && isOwner && !isEditingThis && (
                    <button
                      onClick={() => deleteRole(role.id)}
                      className="hv-btn hv-btn-ghost text-xs text-rose-300 hover:bg-rose-500/10"
                      data-testid={`role-delete-${role.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  )}
                </div>
              </div>

              {!isEditingThis ? (
                <PermissionsView role={role} />
              ) : (
                <PermissionsEditor
                  form={form}
                  setForm={setForm}
                  onTogglePerm={togglePermLevel}
                  onAddResource={addResource}
                  onRemoveResource={removeResource}
                  onToggleParent={toggleParent}
                  roles={roles}
                  onCancel={cancelEdit}
                  onSave={save}
                  saving={saving}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Recent role-actions audit */}
      {recentActions.length > 0 && (
        <section className="hv-card p-4" data-testid="roles-audit-recent">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#1FB6C1]" />
            <h2 className="font-display text-base font-bold">Recent role changes (last 8)</h2>
          </div>
          <ul className="space-y-1.5">
            {recentActions.map((a) => {
              const expired = a.undo_expires_at && new Date(a.undo_expires_at) < new Date();
              return (
                <li key={a.id} className="text-[11px] flex items-center gap-2 border border-white/5 rounded px-2 py-1.5 bg-black/10" data-testid={`audit-${a.id}`}>
                  <span className="font-mono text-[#647087]">{(a.created_at || "").slice(5, 16)}</span>
                  <span className={a.status === "ok" ? "text-emerald-300" : "text-rose-300"}>{a.status}</span>
                  <span className="text-[#C4B5FD]">{a.tool}</span>
                  <span className="text-[#a0aabf]">by {a.asker_name}</span>
                  <span className="text-[#647087] truncate flex-1">{a.result_preview || a.refusal_reason}</span>
                  {a.undoable && a.status === "ok" && !a.undone_at && !expired && (
                    <button
                      onClick={() => undoAction(a.id)}
                      className="hv-btn hv-btn-ghost text-[10px] py-0.5"
                      data-testid={`audit-undo-${a.id}`}
                    >Undo</button>
                  )}
                  {a.undone_at && <span className="text-[10px] text-amber-300">undone</span>}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
    </EntityShell>
  );
}

function PermissionsView({ role }) {
  const entries = Object.entries(role.permissions || {});
  const resolved = role.resolved_permissions || {};
  return (
    <div className="mt-3 space-y-2">
      {entries.length === 0 ? (
        <div className="text-[11px] text-[#647087] italic">No direct permissions — inherits everything from parents.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {entries.map(([resource, levels]) => (
            <div key={resource} className="text-[11px] flex items-center gap-2">
              <span className="font-mono text-[#dde3ec]">{resource}</span>
              <span className="ml-auto flex gap-1">
                {(levels || []).map((l) => (
                  <span key={l} className="text-[10px] uppercase font-mono px-1.5 py-0 rounded bg-[#1FB6C1]/15 border border-[#1FB6C1]/30 text-[#1FB6C1]">{l}</span>
                ))}
              </span>
            </div>
          ))}
        </div>
      )}
      {Object.keys(resolved).length > entries.length && (
        <div className="text-[10px] text-[#647087] mt-2 inline-flex items-center gap-1">
          <Eye className="w-3 h-3" /> Resolved (incl. inheritance): {Object.keys(resolved).length} resources
        </div>
      )}
    </div>
  );
}

function PermissionsEditor({ form, onTogglePerm, onAddResource, onRemoveResource, onToggleParent, roles, onCancel, onSave, saving }) {
  const [newResource, setNewResource] = useState("");
  const usedResources = Object.keys(form.permissions || {});
  const suggestions = COMMON_RESOURCES.filter((r) => !usedResources.includes(r));

  return (
    <div className="mt-3 space-y-3 border-t border-white/10 pt-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Inherits from (max 3)</div>
        <div className="flex flex-wrap gap-1.5">
          {roles.filter((r) => r.id !== form.id).map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => onToggleParent(r.id)}
              className={`text-[11px] px-2 py-0.5 rounded-full border transition ${
                (form.inherits_from || []).includes(r.id)
                  ? "bg-[#A78BFA]/20 text-[#C4B5FD] border-[#A78BFA]/50"
                  : "bg-white/5 text-[#a0aabf] border-white/10 hover:border-[#A78BFA]/30"
              }`}
              data-testid={`role-edit-parent-${r.id}`}
            >{r.name}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Permission grid</div>
        <div className="space-y-1">
          {usedResources.map((resource) => (
            <div key={resource} className="flex items-center gap-2 text-[11px]" data-testid={`perm-row-${resource}`}>
              <span className="font-mono text-[#dde3ec] flex-1 truncate">{resource}</span>
              <div className="flex items-center gap-1">
                {LEVEL_OPTIONS.map((level) => {
                  const has = (form.permissions[resource] || []).includes(level);
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() => onTogglePerm(resource, level)}
                      className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded border transition ${
                        has
                          ? "bg-[#1FB6C1]/15 border-[#1FB6C1]/40 text-[#1FB6C1]"
                          : "bg-white/5 border-white/10 text-[#647087] hover:border-[#1FB6C1]/30"
                      }`}
                      data-testid={`perm-toggle-${resource}-${level}`}
                    >{level}</button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => onRemoveResource(resource)}
                  className="text-[#647087] hover:text-rose-300 ml-1"
                  title="Remove resource"
                ><XIcon className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <input
            type="text"
            placeholder="Add resource (e.g. customers.financials)"
            value={newResource}
            onChange={(e) => setNewResource(e.target.value)}
            className="hv-input text-[11px] flex-1"
            list={`suggest-${form.id}`}
            data-testid="role-edit-add-resource-input"
          />
          <datalist id={`suggest-${form.id}`}>
            {suggestions.map((s) => <option key={s} value={s} />)}
          </datalist>
          <button
            type="button"
            onClick={() => { onAddResource(newResource.trim()); setNewResource(""); }}
            disabled={!newResource.trim()}
            className="hv-btn hv-btn-ghost text-[11px]"
            data-testid="role-edit-add-resource"
          ><Plus className="w-3 h-3" /> Add</button>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
        <button onClick={onSave} disabled={saving} className="hv-btn hv-btn-primary text-xs" data-testid="role-edit-save">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          Save changes
        </button>
      </div>
    </div>
  );
}
